import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, ArrowLeft, Leaf, CheckCircle, Check, X, AlertCircle, Loader2 } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "@/utils/axios";
import LoginAuth from "@/assets/images/auth/login-auth.jpg";

export default function SetupPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [error, setError] = useState("");

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setIsExpired(true);
        setIsValidating(false);
        return;
      }

      try {
        const response = await axios.get("/users/validate-setup-token", {
          params: { token, email }
        });
        
        if (response.data.valid) {
          setFirstName(response.data.firstName || "");
          setIsValidating(false);
        } else {
          setIsExpired(true);
          setIsValidating(false);
        }
      } catch (err) {
        setIsExpired(true);
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, email]);

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const passwordStrength = getPasswordStrength(password);
  
  const getStrengthText = (strength) => {
    if (strength === 0) return "";
    if (strength <= 25) return "Weak";
    if (strength <= 50) return "Fair";
    if (strength <= 75) return "Good";
    return "Strong";
  };

  const getStrengthColor = (strength) => {
    if (strength <= 25) return "bg-red-500";
    if (strength <= 50) return "bg-yellow-500";
    if (strength <= 75) return "bg-blue-500";
    return "bg-green-500";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return;
    if (passwordStrength < 50) return;

    setIsLoading(true);
    setError("");

    try {
      await axios.post("/users/setup-password", {
        token,
        email,
        password
      });
      setIsSuccess(true);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to set up password. Please try again.";
      setError(message);
      if (message.includes("expired") || message.includes("invalid")) {
        setIsExpired(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loading State
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-primary mb-2">Validating your link...</h1>
            <p className="text-gray-600">Please wait while we verify your setup link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Expired Link State
  if (isExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-primary mb-4">Link Expired or Invalid</h1>
            <p className="text-gray-600 mb-8">
              This password setup link has expired or is invalid. Please contact our support team for assistance.
            </p>

            <div className="space-y-4">
              <Link to="/contact">
                <Button className="w-full bg-primary hover:bg-primary/90 text-white py-3">
                  Contact Support
                </Button>
              </Link>

              <Link to="/auth/login" className="block text-gray-600 hover:text-primary font-medium">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Success Illustration */}
          <div className="hidden lg:block">
            <div className="relative">
              <img
                src={LoginAuth}
                alt="Password setup success"
                width={500}
                height={600}
                className="rounded-2xl shadow-2xl object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-500/20 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-2">You're All Set!</h2>
                <p className="text-white/90 max-w-sm">
                  Your account is ready. Start exploring our premium skincare products today.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Success Message */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>

              <h1 className="text-2xl font-bold text-primary mb-4">
                Welcome to Fayoz{firstName ? `, ${firstName}` : ""}!
              </h1>
              <p className="text-gray-600 mb-8">
                Your password has been set up successfully. You can now sign in to your account and start shopping.
              </p>

              <div className="space-y-4">
                <Link to="/auth/login">
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg">
                    Sign In to Your Account
                  </Button>
                </Link>

                <Link to="/shop" className="block text-gray-600 hover:text-primary font-medium">
                  Browse Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Setup Password Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden lg:block">
          <div className="relative">
            <img
              src={LoginAuth}
              alt="Setup password illustration"
              width={500}
              height={600}
              className="rounded-2xl shadow-2xl object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-2xl"></div>
            <div className="absolute bottom-8 left-8 text-white">
              <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Set Up Your Password</h2>
              <p className="text-white/90 max-w-sm">
                Create a secure password to access your account and start your skincare journey with Fayoz.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Setup Form */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-primary">Fayoz</span>
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                Welcome{firstName ? `, ${firstName}` : ""}!
              </h1>
              <p className="text-gray-600">Create a password to access your account</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-primary font-medium">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {password && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600 min-w-12">{getStrengthText(passwordStrength)}</span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex items-center gap-2">
                        {password.length >= 8 ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>At least 8 characters</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/[A-Z]/.test(password) ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>One uppercase letter</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/[0-9]/.test(password) ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>One number</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/[^A-Za-z0-9]/.test(password) ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                        <span>One special character</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-primary font-medium">
                  Confirm Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                    <X className="w-3 h-3" />
                    Passwords do not match
                  </p>
                )}
                {confirmPassword && password === confirmPassword && (
                  <p className="text-green-500 text-xs mt-2 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Passwords match
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading || password !== confirmPassword || passwordStrength < 50}
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Setting Up Password...
                  </div>
                ) : (
                  "Set Up Password"
                )}
              </Button>
            </form>

            {/* Security Note */}
            <div className="mt-6 p-4 bg-secondary/10 border border-secondary/20 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Security tip:</strong> Use a unique password that you don't use for other accounts. Consider
                using a password manager for better security.
              </p>
            </div>

            {/* Back to Login */}
            <div className="text-center mt-8">
              <Link
                to="/auth/login"
                className="inline-flex items-center gap-2 text-secondary hover:text-secondary/80 font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Already have a password? Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
